/**
 * Sets up parallax scrolling for a given element.
 *
 * Requirements:
 * - `overflow: hidden;` on parent element.
 * - The element needs to be larger than its parent (i.e. set a negative `margin-top` and `margin-bottom`).
 *
 * Implementation example:
 * ```
 * var parallax = new EN.Parallax('.no-touch .en-mdl-stage__background', {
 *     size: 10,
 *     invert: true
 * });
 * ```
 *
 * @class Parallax
 * @author EN GARDE (David Hell, Martin Holler)
 * @version 3.0
 *
 * @param {string} selector - Element selector.
 * @param {object} [options] - Overrides defaults, see below.
 * @param {number} options.size - Sum of both of the element's negative margins in em (i.e. 10em if margin-top and margin-bottom are 5em); IMPORTANT: the intensity of the effects can also be set via CSS (by changing font-size).
 * @param {boolean} options.invert - Direction in which the image scrolls; true = with scroll, false = against scroll.
 *
 *
 * Changelog:
 * 	3.0		rewrite: now supports multiple instances
 * 	2.1		fixed "size" property not working (margin was used in code)
 * 	2.0		rewrite
 * 	1.0		initial version
 */

var EN = EN || {};

EN.Parallax = (function (_, JsHelper, MathHelper, $, document, window) {
	'use strict';

	/**
	 * Shared properties
	 * These variables will be shared across all instances.
	 */
	var $w = $(window),
		$bdy = $(document.body);

	/** @constructor */
	function Parallax(selector, options) {
		var that = this;

		// dependency checks
		if (!JsHelper.dependencyExists(_, 'lodash', 'Parallax')) return;
		if (!JsHelper.dependencyExists($, 'jQuery', 'Parallax')) return;
		if (!JsHelper.dependencyExists(document, 'document', 'Parallax')) return;
		if (!JsHelper.dependencyExists(window, 'window', 'Parallax')) return;
		if (!JsHelper.dependencyExists(MathHelper, 'MathHelper', 'Parallax')) return;

		that.selector = selector;
		that.options = _.defaultsDeep(options, that.defaults);
		that.$elements = $(); // see init
		that.animationFrameId;
		that.lastScrollPosition = $w.scrollTop();

		$(function() {
			that.init();
		});
	}


	/**
	 * Default options
	 * @memberof Parallax
	 */
	Parallax.prototype.defaults = {
		size: 10,
		invert: false
	};


	/**
	 * Sets up the Parallax effect.
	 * Creates a `requestAnimationFrame` loop in which update() gets called.
	 * @memberof Parallax
	 * @return {boolean} true if `requestAnimationFrame` is supported, false if not.
	 */
	Parallax.prototype.init = function () {
		var that = this;

		that.$elements = $(that.selector);
		that.update();

		if (window.requestAnimationFrame) {
			// if there is already a loop running, reset it
			window.cancelAnimationFrame(that.animationFrameId);

			(function loop () {
				that.animationFrameId = window.requestAnimationFrame(loop);

				// bail if there are no valid elements
				if (that.$elements && !that.$elements.length) {
					window.cancelAnimationFrame(that.animationFrameId);

					return true;
				}

				// requestAnimationFrame runs continuously; only update the parallax if the scroll position has changed
				if ($w.scrollTop() !== that.lastScrollPosition) {
					that.update();
					that.lastScrollPosition = $w.scrollTop();
				}
			}());

			return true;
		}
		else {
			return false;
		}

	};

	/**
	 * Sets the `translate3d` property of the supplied elements.
	 * @memberof class:Parallax
	 * @return {boolean} true if elements were updated.
	 */
	Parallax.prototype.update = function () {
		var that = this;

		if (that.$elements && that.$elements.length) {
			that.$elements.each(function () {
				var $this = $(this);

				var offset = $this.get(0).getBoundingClientRect(),
					position = MathHelper.mapValue(offset.top, -offset.height, $(window).height(), that.options.size / 2 * -1 , that.options.size / 2, true);

				position = position.toFixed(3);

				if (!that.options.invert) {
					position = -position;
				}

				$this.css({
					transform: 'translate3d(0, ' + position + 'em, 0)'
				});
			});

			return true;
		}

		return false;
	};


	return Parallax;

}(_, EN.JsHelper, EN.MathHelper, jQuery, document, window));
